import React from "react";
import { Handle, Position } from "reactflow";
import "../../App.css";
import { getYearsBetweenDates, msToDate } from "../../Utils/dateAndTime";
import { getGenerationName } from "../../Utils/generation";
import { getTextColor } from "../../Utils/colors";
import { renderNameWithBoldFirst, formatNameWithSuffix, formatSuffix, getShortDisplayName } from "../../Utils/name";

const CONTACT_ADMIN_TOOLTIP = "Any changes to your profile data, contact the Admin by clicking the “Contact Admin” button at the top of the screen.";

/** Deceased angel icon next to names on Start/Top of the Line (generation 1) tree nodes */
const GEN1_ANGEL_ICON_PX = 40;

const FamilyMemberNode = ({ data, onAddChild, onEdit, onView, selectedGeneration = null, selectedFamilyLineColor = null, onReorderMultipleBirth = null, canAddMembers = true }) => {
  const {
    firstName,
    special_name,
    parent_special_name,
    lastName,
    photo,
    spouse_image,
    relation,
    colorCode,
    isRootHead,
    generation,
    middleName,
    suffix,
    gender,
    spouse_first_name,
    spouseFirstName,
    spouse_last_name,
    spouseLastName,
    spouse_middle_name,
    spouseMiddleName,
    dateOfDeath,
    spouseGender,
    present_or_deceased,
    aliveStatus,
    spouseAliveStatus,
    spouseAgeOfDeath,
    spouseDateOfDeath,
    spouseDob,
    dob,
    spouseFullName,
    parent_name,
    parent_hex_code,
    parent_color_name,
    spouseImage,
    maritalStatus,
    cell_type,
    spouse_cell_type,

    spouseSuffix,
    spouseSpecialName,
    descendantCount,
    descendantMainCount,
    descendantMalesCount,
    descendantFemalesCount,
    descendantSpousesCount,
    isDeepestInLine,
    twinsTripletsSetsCount,
    twinsTripletsType,
    twinsTripletsBirthOrder,
    id: memberId,
    user_id: userId,
  } = data;



  // Extract suffix value (handle both string and object formats)
  const suffixValue = suffix?.name || suffix || "";
  
  // Full name (first + middle + last + suffix) for generation badge etc.
  const fullName = formatNameWithSuffix(firstName, middleName, lastName, suffixValue);

  // Short display name for node: first name then suffix if has, else special name if has (Heads of Line format)
  const displayNameShort = getShortDisplayName(firstName, suffixValue, special_name);

  // Construct spouse full name from parts if available, otherwise use spouseFullName from data
  const spouseFirst = spouseFirstName || spouse_first_name;
  const spouseMiddle = spouseMiddleName || spouse_middle_name;
  const spouseLast = spouseLastName || spouse_last_name;
  const spouseSuffixValue = spouseSuffix?.name || spouseSuffix || "";
  const spouseSpecialNameVal = spouseSpecialName || data.spouse_special_name || "";
  const spouseMaiden = String(data.spouseMaidenName || data.spouse_maiden_name || "").trim();

  /** Start/Top of the Line (gen 1): spouse as "First Middle (Maiden) Last, Suffix" when maiden is present. */
  const spouseFullNameFormatted = (() => {
    if (generation === 1 && spouseMaiden && (spouseFirst || spouseLast)) {
      const beforeMaiden = [spouseFirst, spouseMiddle]
        .filter((p) => p != null && String(p).trim() !== "")
        .join(" ");
      let base = beforeMaiden
        ? `${beforeMaiden} (${spouseMaiden})`
        : `(${spouseMaiden})`;
      if (spouseLast) base = `${base} ${spouseLast}`.trim();
      if (spouseSuffixValue) {
        const formattedSuffix = formatSuffix(spouseSuffixValue);
        return base ? `${base}, ${formattedSuffix}` : formattedSuffix;
      }
      return base;
    }
    if (spouseFirst || spouseLast) {
      return formatNameWithSuffix(spouseFirst, spouseMiddle, spouseLast, spouseSuffixValue);
    }
    return spouseFullName;
  })();

  // Short display name for spouse (same format as member)
  const spouseDisplayNameShort = (spouseFirst || spouseLast)
    ? getShortDisplayName(spouseFirst, spouseSuffixValue, spouseSpecialNameVal)
    : (spouseFullName || "");
  
  const showHiddenClass = (aliveStatus == 1 && spouseAliveStatus == 1 && generation == 1) ? "hidden-angel-img" : "";

  // Helper function to darken a color for gradient effect
  const darkenColor = (hex, percent = 20) => {
    if (!hex) return "#4f46e5";

    // Remove # if present
    hex = hex.replace("#", "");

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken
    const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));

    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG
      .toString(16)
      .padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
  };

  // Helper function to lighten a color
  const lightenColor = (hex, percent = 40) => {
    if (!hex) return "#a5b4fc";

    // Remove # if present
    hex = hex.replace("#", "");

    // Handle short hex like #abc
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Lighten by moving towards white (255, 255, 255)
    const lightenedR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
    const lightenedG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
    const lightenedB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

    // Convert back to hex
    return `#${lightenedR.toString(16).padStart(2, "0")}${lightenedG
      .toString(16)
      .padStart(2, "0")}${lightenedB.toString(16).padStart(2, "0")}`;
  };

  const handleAddChild = () => {
    if (!canAddMembers) return;
    // Trigger parent callback to open modals
    if (onAddChild) {
      onAddChild(data);
    }
  };

  function hexToRgb(hex) {
    if (!hex) return "146, 208, 80"; // fallback in case colorCode is undefined
    hex = hex.replace("#", "");

    // handle short hex like #abc
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `${r}, ${g}, ${b}`;
  }

  const handleEdit = () => {
    if (!canAddMembers) return;
    if (onEdit) {
      onEdit(data);
    }
  };

  const handleView = () => {
    if (!canAddMembers) return;
    // Trigger parent callback to open view modal
    if (onView && data) {
      onView(data);
    }
  };

  const baseColor = colorCode || "#6366f1";
  const darkerColor = darkenColor(baseColor, 15);

  // Check if this is Gen 2 (Head of Family Line/Color)
  const isGen2 = generation === 2 || data.generation_level === 2;

  const resolvedGeneration = Number(generation ?? data.generation_level ?? 0);
  const isThirdGenerationOrDeeper = resolvedGeneration > 2;

  const generationClass = generation === 1 ? "main-div" : "child";
  const isFirstNodeClass = generation === 1 ? "first-generation-node" : "";
  const generationClassContent = generation === 1 ? "main-inner-div first-generation-main-inner" : "inner-child";
  const isGenerationHighlighted = (selectedGeneration) => {
    // If no generation is selected, don't apply any opacity changes
    if (selectedGeneration === null || selectedGeneration === undefined || selectedGeneration === '') {
      return true;
    }

    // If a generation is selected, check if it matches the current node's generation
    return Number(selectedGeneration) == generation;
  }

  const isColorSelected = (selectedFamilyLineColor) => {
    // If no color is selected, don't apply any opacity changes
    if (selectedFamilyLineColor === null || selectedFamilyLineColor === undefined || selectedFamilyLineColor === '') {
      return true;
    }

    // If a color is selected, check if it matches the current node's color
    return selectedFamilyLineColor === colorCode;
  }

  const handleFiltersForColorAndGeneration = (selectedGen, selectedColor) => {
    // If no filters are selected, show everything
    if ((!selectedGen || selectedGen == '') && (!selectedColor || selectedColor == '')) {
      return true;
    }

    // If only generation is selected
    if (selectedGen && (!selectedColor || selectedColor == '')) {
      return Number(selectedGen) == generation;
    }

    // If only color is selected
    if (selectedColor && (!selectedGen || selectedGen == '')) {
      return selectedColor == colorCode;
    }

    // If both generation and color are selected
    // Show if both conditions match
    return Number(selectedGen) == generation && selectedColor == colorCode;
  }

  const renderFooterButtons = () => (
    <>
      {/* View (eye) — admins only; hidden for users to avoid disabled button + contact-admin tooltip */}
      {canAddMembers && (
        <div className="node-action-btn-tooltip-wrapper">
          <div className="node-action-btn-tooltip">View this member&apos;s details</div>
          <button
            className="node-action-btn brn-left"
            style={{ borderColor: colorCode, background: colorCode || "#ccc" }}
            onClick={handleView}
            aria-label="View Details"
          >
            <img
              src={window.getPublicUrl("/assets/images/eye-view.svg")}
              alt="View Details"
            />
          </button>
        </div>
      )}

      {generation === 1 && (
        <div className={`node-action-btn-tooltip-wrapper ${!canAddMembers ? "node-action-btn-tooltip-wrapper--contact-admin" : ""}`}>
          <div className={`node-action-btn-tooltip ${!canAddMembers ? "contact-admin-msg" : ""}`}>
            {canAddMembers ? "Edit this member's information" : CONTACT_ADMIN_TOOLTIP}
          </div>
          <button
            type="button"
            className={`node-action-btn ${!canAddMembers ? "node-action-btn-disabled" : ""}`}
            onClick={handleEdit}
            disabled={!canAddMembers}
            aria-label={canAddMembers ? "Edit member" : "Contact admin to view or make changes"}
            style={{
              borderColor: colorCode,
              background: colorCode || "#ccc",
            }}
          >
            <img
              src={window.getPublicUrl("/assets/images/edit-view.svg")}
              alt="Edit"
            />
          </button>
        </div>
      )}

      {/* Add child — admins only; hidden for gen 1 (uses + below card) and for regular users */}
      {canAddMembers && generation !== 1 && (
        <div className="node-action-btn-tooltip-wrapper">
          <div className="node-action-btn-tooltip">Add a child to this member</div>
          <button
            className="node-action-btn brn-right"
            onClick={handleAddChild}
            style={{ borderColor: colorCode, background: colorCode || "#ccc" }}
            aria-label="Add Child"
          >
            <img
              src={window.getPublicUrl("/assets/images/add-new-uer.svg")}
              alt="Add Child"
            />
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className={`family-member-node-wrapper ${generationClass} ${isFirstNodeClass}`}>
      <div
        className="family-member-node"
        style={{
          // backgroundColor: `rgba(${hexToRgb(colorCode)}, 0.5)`,
          border: `3px solid ${colorCode}`,
          opacity: (selectedGeneration || selectedFamilyLineColor) ? (handleFiltersForColorAndGeneration(selectedGeneration, selectedFamilyLineColor) ? 1 : 0.2) : 1,
          position: 'relative',
          ...(isGen2 && {
            backgroundColor: lightenColor(colorCode, 75)
          })
        }}
      >
        {/* Top Handle - for parents (invisible on gen 2: hide connector dot, edges still attach) */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{
            background: isGen2 ? "transparent" : colorCode || "#555",
            width: 8,
            height: 8,
            opacity: isGen2 ? 0 : 0.5,
            border: isGen2 ? "none" : undefined,
            boxShadow: isGen2 ? "none" : undefined,
          }}
        />

        {/* Twins/Triplets — top-right of card (gen 2+ only) */}
        {twinsTripletsSetsCount > 0 && twinsTripletsType && generation > 1 && (
          <div
            className="twins-triplets-indicator twins-triplets-indicator--top-right"
            style={{
              position: "absolute",
              top: "6px",
              right: "6px",
              left: "auto",
              zIndex: 11,
              pointerEvents: onReorderMultipleBirth ? "auto" : "none",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              cursor: onReorderMultipleBirth ? "pointer" : "default",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onReorderMultipleBirth) {
                onReorderMultipleBirth({
                  memberId,
                  userId,
                  birthType: twinsTripletsType,
                  setNumber: twinsTripletsSetsCount,
                  currentOrder: twinsTripletsBirthOrder,
                });
              }
            }}
          >
            <div
              style={{
                backgroundColor:
                  gender === 1
                    ? "#0000FF"
                    : gender === 2
                      ? "#FF0000"
                      : colorCode || "#6366f1",
                color: "#FFFFFF",
                padding: "3px 8px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: "bold",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                lineHeight: "1.2",
              }}
            >
              <span style={{ textTransform: "none" }}>
                {twinsTripletsType === "triplets"
                  ? "Trip"
                  : twinsTripletsType === "quads"
                    ? "Quad"
                    : twinsTripletsType === "quins"
                      ? "Quin"
                      : twinsTripletsType === "sexs"
                        ? "Sex"
                        : twinsTripletsType === "seps"
                          ? "Sep"
                          : twinsTripletsType === "ocs"
                            ? "Oc"
                            : twinsTripletsType === "nuns"
                              ? "Non"
                              : "Twin"}{" "}
                {twinsTripletsBirthOrder > 0 ? twinsTripletsBirthOrder : ""}
              </span>
            </div>
          </div>
        )}

        {/* Node Content */}
        <div className={`${generationClassContent}`}>
          {/* Image circles - only show for Generation 1 */}
          {generation === 1 && (
            <div
              className="node-header"
            // style={{
            //   background: colorCode|| '#ccc'
            // }}
            >
              {/* {isRootHead && (
              <div className="root-head-badge">
                <i className="fas fa-crown"></i>
              </div>
            )} */}
              <div className="member-photo grand-fa-chld" style={{ borderColor: colorCode }}>
                {photo ? (
                  <img src={photo} alt={fullName} />
                ) : (
                  <div className="photo-placeholder">
                    <img
                      src={window.getPublicUrl("/assets/images/dummy-user.svg")}
                      alt="Toggle Password"
                    />
                  </div>
                )}
              </div>
              {
                // only show spouse image if main member is not single/divorced
                (maritalStatus && (maritalStatus != 1)
                  && (spouseFirstName)
                ) && (
                  <div className="member-photo grand-ma-child" style={{ borderColor: colorCode }}>
                    {spouse_image || spouseImage ? (
                      <img src={spouse_image || spouseImage} alt={`${spouse_first_name || ''} ${spouse_last_name || ''}`.trim() || 'Spouse'} />
                    ) : (
                      <div className="photo-placeholder">
                        <img
                          src={window.getPublicUrl("/assets/images/dummy-user.svg")}
                          alt="Toggle Password"
                        />
                      </div>
                    )}
                  </div>
                )
              }

            </div>
          )}
          
          {/* Commented out image circles for Generation 2+ */}
          {/* {generation !== 1 && (
            <div
              className="node-header"
            >
              <div className="member-photo grand-fa-chld" style={{ borderColor: colorCode }}>
                {photo ? (
                  <img src={photo} alt={fullName} />
                ) : (
                  <div className="photo-placeholder">
                    <img
                      src={window.getPublicUrl("/assets/images/dummy-user.svg")}
                      alt="Toggle Password"
                    />
                  </div>
                )}
              </div>
              {
                (maritalStatus && (maritalStatus != 1)
                  && (spouseFirstName)
                ) && (
                  <div className="member-photo grand-ma-child" style={{ borderColor: colorCode }}>
                    {spouse_image || spouseImage ? (
                      <img src={spouse_image || spouseImage} alt={`${spouse_first_name || ''} ${spouse_last_name || ''}`.trim() || 'Spouse'} />
                    ) : (
                      <div className="photo-placeholder">
                        <img
                          src={window.getPublicUrl("/assets/images/dummy-user.svg")}
                          alt="Toggle Password"
                        />
                      </div>
                    )}
                  </div>
                )
              }
            </div>
          )} */}

          <div 
            className={`node-body ${showHiddenClass}`} 
            style={{ 
              borderColor: colorCode,
              ...(generation !== 1 && {
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: isThirdGenerationOrDeeper ? 'center' : 'flex-start',
                padding: '6px 6px',
                ...(isGen2 && { paddingTop: '20px' }),
                ...(isThirdGenerationOrDeeper && {
                  flex: 1,
                  minHeight: 0,
                  alignSelf: 'stretch',
                }),
                position: 'relative'
              })
            }}
          >
            {/* Container for names - always starts at same position */}
            <div style={{
              marginTop: generation === 1 ? '6px' : isThirdGenerationOrDeeper ? '0' : '2px',
              ...(generation !== 1 && {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: '0px',
                lineHeight: '1'
              })
            }}>
              {/* main member name section */}
              <h4
                className={`member-name ${!dateOfDeath ? "blank-angel" : ""} ${(dateOfDeath && aliveStatus === 2 && generation !== 1) ? "member-name--date-below" : ""}`}
                style={{
                  ...(generation !== 1 && {
                    textAlign: 'center',
                    width: '100%',
                    margin: '0',
                    marginTop: '0',
                    marginBottom: '0',
                    marginLeft: '0',
                    marginRight: '0',
                    lineHeight: '1'
                  })
                }}
              >
                {(dateOfDeath && aliveStatus === 2) ? (
                  generation === 1 ? (
                    <span className="angel">
                      <img
                        src={window.getPublicUrl("/assets/images/angel.png")}
                        alt="main angel"
                        style={{
                          opacity: 0.9,
                          width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                          height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                          margin: "0",
                          marginRight: "0",
                          marginLeft: "0",
                          padding: "0",
                          paddingRight: "0",
                          paddingLeft: "0",
                          verticalAlign: "middle",
                          display: "inline",
                        }}
                      />
                      {renderNameWithBoldFirst(fullName, gender)}
                      <span className="age ps-1" style={{ color: gender == 1 ? "#0000FF" : "#FF0000" }}>
                        ({getYearsBetweenDates(dateOfDeath, dob)})
                      </span>
                    </span>
                  ) : (
                    <span
                      className="angel"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0,
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <img
                          src={window.getPublicUrl("/assets/images/angel.png")}
                          alt="main angel"
                          style={{
                            opacity: 0.9,
                            width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            margin: "0",
                            marginRight: "0",
                            marginLeft: "0",
                            padding: "0",
                            paddingRight: "0",
                            paddingLeft: "0",
                            verticalAlign: "middle",
                            display: generation !== 1 ? "block" : "inline",
                          }}
                        />
                        {renderNameWithBoldFirst(fullName, gender)}
                      </span>
                      <span
                        className="age member-node-date-line"
                        style={{
                          color: gender == 1 ? "#0000FF" : "#FF0000",
                          marginTop: generation !== 1 ? "2px" : "4px",
                        }}
                      >
                        ({getYearsBetweenDates(dateOfDeath, dob)})
                      </span>
                    </span>
                  )
                ) : (
                  <span
                    className={aliveStatus == 2 ? "angel" : ""}
                    style={{
                      ...(generation !== 1
                        ? {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0",
                            margin: "0",
                            padding: "0",
                            paddingInline: "0",
                            marginRight: "0",
                            marginLeft: "0",
                          }
                        : {}),
                    }}
                  >
                    {(aliveStatus == 2) && (
                      <img
                        src={window.getPublicUrl("/assets/images/angel.png")}
                        alt="main angel"
                        style={{
                          opacity: 0.9,
                          width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                          height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                          margin: "0",
                          marginRight: "0",
                          marginLeft: "0",
                          padding: "0",
                          paddingRight: "0",
                          paddingLeft: "0",
                          verticalAlign: "middle",
                          display: generation !== 1 ? "block" : "inline",
                        }}
                      />
                    )}
                    {(aliveStatus == 1 && generation == 1) ? (
                      <span className={`${showHiddenClass ? "" : "blank-angel-img"}`}>
                        {renderNameWithBoldFirst(fullName, gender)}
                      </span>
                    ) : (
                      renderNameWithBoldFirst(fullName, gender)
                    )}
                  </span>
                )}
              </h4>

            <h4
              className={`member-name ${!spouseDateOfDeath ? "blank-angel" : ""} ${(spouseDateOfDeath && spouseAliveStatus == 2 && generation !== 1) ? "member-name--date-below" : ""}`}
              style={{
                ...(generation !== 1 && {
                  textAlign: 'center',
                  width: '100%',
                  margin: '0',
                  marginTop: '0',
                  marginBottom: '0',
                  marginLeft: '0',
                  marginRight: '0',
                  lineHeight: '1'
                })
              }}
            >
              {/* Show spouse information if spouse exists */}
              {(spouseFullNameFormatted || (maritalStatus && maritalStatus != 1)) && (
                <>
                  {(spouseDateOfDeath && spouseAliveStatus == 2) ? (
                    generation === 1 ? (
                      <span className="angel">
                        <img
                          src={window.getPublicUrl("/assets/images/angel.png")}
                          alt="spouse angel"
                          style={{
                            opacity: 0.9,
                            width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            margin: "0",
                            marginRight: "0",
                            marginLeft: "0",
                            padding: "0",
                            paddingRight: "0",
                            paddingLeft: "0",
                            verticalAlign: "middle",
                            display: "inline",
                          }}
                        />
                        {renderNameWithBoldFirst(spouseFullNameFormatted, spouseGender)}
                        <span className="age ps-1" style={{ color: spouseGender == 1 ? "#0000FF" : "#FF0000" }}>
                          ({getYearsBetweenDates(spouseDateOfDeath, spouseDob)})
                        </span>
                      </span>
                    ) : (
                      <span
                        className="angel"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0,
                            margin: 0,
                            padding: 0,
                          }}
                        >
                          <img
                            src={window.getPublicUrl("/assets/images/angel.png")}
                            alt="spouse angel"
                            style={{
                              opacity: 0.9,
                              width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                              height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                              margin: "0",
                              marginRight: "0",
                              marginLeft: "0",
                              padding: "0",
                              paddingRight: "0",
                              paddingLeft: "0",
                              verticalAlign: "middle",
                              display: generation !== 1 ? "block" : "inline",
                            }}
                          />
                          {renderNameWithBoldFirst(spouseFullNameFormatted, spouseGender)}
                        </span>
                        <span
                          className="age member-node-date-line"
                          style={{
                            color: spouseGender == 1 ? "#0000FF" : "#FF0000",
                            marginTop: generation !== 1 ? "2px" : "4px",
                          }}
                        >
                          ({getYearsBetweenDates(spouseDateOfDeath, spouseDob)})
                        </span>
                      </span>
                    )
                  ) : (
                    <span
                      className={spouseAliveStatus == 2 ? "angel" : ""}
                      style={{
                        ...(generation !== 1
                          ? {
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0",
                              margin: "0",
                              padding: "0",
                              paddingInline: "0",
                              marginRight: "0",
                              marginLeft: "0",
                            }
                          : {}),
                      }}
                    >
                      {(spouseAliveStatus == 2) && (
                        <img
                          src={window.getPublicUrl("/assets/images/angel.png")}
                          alt="spouse angel"
                          style={{
                            opacity: 0.9,
                            width: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            height: generation === 1 ? `${GEN1_ANGEL_ICON_PX}px` : "16px",
                            margin: "0",
                            marginRight: "0",
                            marginLeft: "0",
                            padding: "0",
                            paddingRight: "0",
                            paddingLeft: "0",
                            verticalAlign: "middle",
                            display: generation !== 1 ? "block" : "inline",
                          }}
                        />
                      )}
                      {(spouseAliveStatus == 1 && generation == 1) ? (
                        <span className={`${showHiddenClass ? "" : "blank-angel-img"}`}>
                          {renderNameWithBoldFirst(spouseFullNameFormatted, spouseGender)}
                        </span>
                      ) : (
                        renderNameWithBoldFirst(spouseFullNameFormatted, spouseGender)
                      )}
                    </span>
                  )}
                </>
              )}
            </h4>
            </div>
            {generation && !isGen2 && (
              <span
                className="generation-badge"
                style={{
                  backgroundColor: `rgb(${hexToRgb(lightenColor(colorCode))})`,
                  border: `2px solid ${colorCode}`,
                  // color:getTextColor(colorCode),
                  color: colorCode === "#080f1f" ? "#FFFFFF" : "#080f1f"
                }}
              >
                {generation === 1
                  ? getGenerationName(generation, parent_name, fullName, false, false, "", "", "", special_name, parent_special_name)
                  : (data.headOfLineBadgeName || displayNameShort)}
              </span>
            )}
          </div>

          {generation === 1 && (
            <div
              className="node-footer node-footer-gen1-inline node-footer-gen1-centered node-footer-gen1-fullwidth"
              style={{ borderColor: colorCode, justifyContent: 'center' }}
            >
              {renderFooterButtons()}
            </div>
          )}

          {generation !== 1 && canAddMembers && (
            <div className="node-footer" style={{ borderColor: colorCode }}>
              {renderFooterButtons()}
            </div>
          )}

        </div>

        {/* Corner decorations */}
        {/* <div
          className="corner-decoration top-left"
          style={{ borderColor: colorCode }}
        ></div>
        <div
          className="corner-decoration top-right"
          style={{ borderColor: colorCode }}
        ></div>
        <div
          className="corner-decoration bottom-left"
          style={{ borderColor: colorCode }}
          onClick={handleView}
        ></div>
        <div
          className="corner-decoration bottom-right"
          style={{ borderColor: colorCode }}
          onClick={handleAddChild}
        ></div> */}

        {/* Bottom Handle — invisible after gen 1 (edges still connect) */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{
            background: generation !== 1 ? "transparent" : colorCode || "#555",
            width: 8,
            height: 8,
            opacity: generation !== 1 ? 0 : 0.5,
            border: generation !== 1 ? "none" : undefined,
            boxShadow: generation !== 1 ? "none" : undefined,
          }}
        />
      </div>

      {/* Add Child below card — generation 1 (Start/Top of the Line), admins only */}
      {canAddMembers && generation === 1 && (
        <div className="node-action-btn-tooltip-wrapper add-child-below-tooltip-wrapper">
          <div className="node-action-btn-tooltip add-child-below-tooltip">Add a child to this member</div>
          <button
            className="add-child-btn-below"
            onClick={handleAddChild}
            aria-label="Add child"
          >
            +
          </button>
        </div>
      )}

      {/* Display descendant count below the deepest node in each color line - positioned below plus icon */}
      {isDeepestInLine && descendantMainCount !== null && descendantMainCount !== undefined && (
        <div 
          style={{ 
            position: 'absolute',
            bottom: '-120px', // Position below the plus icon with more spacing for multi-line content
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#333',
            zIndex: 5,
            lineHeight: '1'
          }}
        >
          <div>
            ({descendantMainCount}) Descendant(s)
          </div>
          <div style={{ whiteSpace: 'nowrap' }}>
            ({descendantMalesCount || 0}) <span style={{ color: '#0000FF', fontWeight: 900 }}>Male(s)</span>, ({descendantFemalesCount || 0}) <span style={{ color: '#FF0000', fontWeight: 900 }}>Female(s)</span>
          </div>
          <div>
            ({descendantSpousesCount || 0}) In-Law(s)
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyMemberNode;
